import { Coordinate } from 'ol/coordinate'
import { LineString, MultiPoint, Point, Polygon } from 'ol/geom'
import VectorLayer from 'ol/layer/Vector'
import { toLonLat } from 'ol/proj'
import VectorSource from 'ol/source/Vector'
import { Fill, Stroke, Style } from 'ol/style'
import CircleStyle from 'ol/style/Circle'
import { FC, Fragment, ReactNode, useEffect, useMemo, useState } from 'react'

import { ILonLat } from '@griegconnect/krakentools-kmap'

import { MeasureObject } from '../controls/MeasureToolControl'
import { useMapContext } from '../MapContext'
import { MeasureProvider } from '../MeasuringContext'
import { MooringPlanContext } from '../MooringPlanContext'
import { DrawMooringLineInteraction } from './DrawMooringLineInteraction'
import DrawMeasureAngleInteraction from './Measure/DrawMeasureAngleInteraction'
import DrawMeasureAreaInteraction from './Measure/DrawMeasureAreaInteraction'
import DrawMeasureCoordinateInteraction from './Measure/DrawMeasureCoordinateInteraction'
import DrawMeasureLineInteraction from './Measure/DrawMeasureLineInteraction'
import MeasureAngle from './Measure/MeasureAngle'
import MeasureArea from './Measure/MeasureArea'
import MeasureCoordinate from './Measure/MeasureCoordinate'
import MeasureLine from './Measure/MeasureLine'
import MooringLine from './MooringLine'
import { StaticVessel } from './StaticVessel'
import { Feature } from 'ol'
import { Extent, createEmpty, extend } from 'ol/extent'

export type MooringLine = {
  id: string
  startPoint: ILonLat
  vertexIndex: number
}

export type MooredVessel = {
  id: string | number
  length: number
  width: number
  depth: number | null
  mooredPosition: ILonLat
  rotation: number
  mooringLines?: MooringLine[]
  isNeighbour?: boolean
  label?: ReactNode
}
export type DrawTypes = 'mooringLine' | MeasureObject['type'] | null

type MooringPlanProps = {
  mooredVessels: MooredVessel[]
  measureObjects?: MeasureObject[]
  disableInteractions?: boolean
  drawMode?: DrawTypes
  onMooringLineCreated?: (startPoint: ILonLat, vesselVertexIndex: number, vesselId: string | number, id: string) => void
  onMeasureObjectCreated?: (newMeasureObject: MeasureObject) => void
  onChangeMooredVessels?: (mooredVessels: MooredVessel[], haveCollision: boolean) => void
  onChangeMeasureObjects?: (measureObjects: MeasureObject[]) => void
  onClicked?: (selectedItem: SelectedMooringPlanItem | null) => void
  selectedItem?: SelectedMooringPlanItem | null
  showLabels?: boolean
  /*
   * Automatically zoom to fit the mooring plan when it is rendered the first time.
   */
  zoomToFit?: boolean
}

export type MooredVesselWithGeometry = MooredVessel & {
  geometry?: Coordinate[]
}

const mooredVesselStyle = {
  fillColor: 'rgba(143, 143, 143, 0.5) ',
  strokeColor: 'white',
  strokeWidth: 1,
}
const neighbouringVesselStyle = {
  fillColor: 'rgba(143, 143, 143, 0.38)',
  strokeColor: 'rgba(228,228,228,1)',
  strokeWidth: 1,
}

export type SelectedMooringPlanItem = {
  type: DrawTypes | 'vessel'
  id: string | number
}

export const MooringPlan: FC<React.PropsWithChildren<MooringPlanProps>> = (props) => {
  const { kmapInstance } = useMapContext()
  const [isColliding, setIsColliding] = useState(false)

  const mooringLayer = useMemo(() => {
    const layer = new VectorLayer<VectorSource<Feature<Polygon | LineString | Point>>>({
      source: new VectorSource(),
      updateWhileInteracting: true,
      updateWhileAnimating: true,
      zIndex: 100,
      style: [
        new Style({
          stroke: new Stroke({
            color: 'black',
            width: 4,
            lineCap: 'round',
          }),
        }),
        new Style({
          image: new CircleStyle({
            radius: 6,
            fill: new Fill({
              color: '#3399CC',
            }),
            stroke: new Stroke({
              color: '#fff',
              width: 2,
            }),
          }),
          geometry: (feature) => {
            const coords = (feature.getGeometry() as LineString)?.getCoordinates?.()
            return coords ? new MultiPoint(coords) : undefined
          },
        }),
      ],
    })
    layer.set('type', 'mooringLayer')
    kmapInstance.addOlLayer(layer)
    return layer
  }, [])

  const measuringLayer = useMemo(() => {
    const layer = new VectorLayer<VectorSource<Feature<LineString>>>({
      source: new VectorSource(),
      updateWhileAnimating: true,
      updateWhileInteracting: true,
      zIndex: 3000,
      style: [
        new Style({
          stroke: new Stroke({
            color: 'blue',
            width: 4,
          }),
        }),
        new Style({
          image: new CircleStyle({
            radius: 6,
            fill: new Fill({
              color: '#3399CC',
            }),
            stroke: new Stroke({
              color: '#fff',
              width: 2,
            }),
          }),
        }),
      ],
    })
    kmapInstance.addOlLayer(layer)
    return layer
  }, [])

  const handleVesselChanged = (
    vesselId: string | number,
    position: Coordinate,
    rotation: number,
    isColliding?: boolean
  ) => {
    const newMooredVessels = props.mooredVessels.map((v) => {
      if (v.id === vesselId) {
        const lonlat = toLonLat(position)
        v.mooredPosition = { lon: lonlat[0], lat: lonlat[1] }
        v.rotation = rotation
      }
      return v
    })
    props.onChangeMooredVessels?.(newMooredVessels, !!isColliding)
    setIsColliding(!!isColliding)
  }

  const handleAddMeasureObject = (object: MeasureObject) => {
    props.onMeasureObjectCreated?.(object)
  }

  const handleMooringLineCreated = (
    startPoint: Coordinate,
    vesselVertexIndex: number,
    vesselId: string | number,
    id: string
  ) => {
    const lonLat = toLonLat(startPoint)
    props.onMooringLineCreated?.({ lon: lonLat[0], lat: lonLat[1] }, vesselVertexIndex, vesselId, id)
  }

  const renderDrawModes = () => {
    switch (props.drawMode) {
      case null:
        return null
      case 'mooringLine':
        return <DrawMooringLineInteraction onMooringLineCreated={handleMooringLineCreated} />
      case 'distance':
        return <DrawMeasureLineInteraction onMeasureLineCreated={handleAddMeasureObject} />
      case 'area':
        return <DrawMeasureAreaInteraction onMeasureAreaCreated={handleAddMeasureObject} />
      case 'angle':
        return <DrawMeasureAngleInteraction onMeasureAngleCreated={handleAddMeasureObject} />
      case 'coordinate':
        return <DrawMeasureCoordinateInteraction onMeasureCoordinateCreated={handleAddMeasureObject} />
      default:
        break
    }
  }

  useEffect(
    () => () => {
      if (kmapInstance) {
        kmapInstance.removeOlLayer(mooringLayer)
        kmapInstance.removeOlLayer(measuringLayer)
      }
    },
    [kmapInstance]
  )

  useEffect(() => {
    if (props.zoomToFit) {
      setTimeout(() => {
        const features = mooringLayer.getSource()?.getFeatures()
        let extent: Extent = createEmpty()
        let doZoom = false
        if (features) {
          features.forEach((feature) => {
            const geometry = feature.getGeometry()
            if (geometry) {
              const featureExtent = geometry.getExtent()
              if (featureExtent) {
                doZoom = true
                extent = extend(extent, featureExtent)
              }
            }
          })
        }
        if (doZoom && extent) {
          kmapInstance.getView().fit(extent, { padding: [50, 50, 50, 50] })
        }
      }, 200)
    }
  }, [props.zoomToFit, kmapInstance])

  useEffect(() => {
    let eventId: string
    if (props.drawMode === null) {
      eventId = kmapInstance.on('mooringLayer_click', (evt) => {
        if (evt.feature.properties.type && props.drawMode === null) {
          props.onClicked?.({ type: evt.feature.properties.type, id: evt.feature.properties.id })
        }
      })
    }
    return () => {
      if (eventId) kmapInstance.un(eventId)
    }
  }, [props.drawMode, props.onClicked])

  useEffect(() => {
    if (props.onClicked) {
      const eventId = kmapInstance.on('backgroundLayer_click', (evt) => {
        props.onClicked!(null)
      })
      return () => {
        kmapInstance.un(eventId)
      }
    }
  }, [kmapInstance, props.onClicked])

  const handleChangeMooringLine = (vessel: MooredVessel, mooringLine: MooringLine, from: ILonLat, toIndex: number) => {
    props.onChangeMooredVessels?.(
      props.mooredVessels.map((v) => {
        if (v === vessel) {
          const lines: MooringLine[] | undefined = v.mooringLines?.map((l) => {
            if (l.id === mooringLine.id) {
              return {
                startPoint: from,
                vertexIndex: toIndex,
                id: mooringLine.id,
              }
            } else {
              return l
            }
          })
          return { ...vessel, mooringLines: lines }
        } else {
          return v
        }
      }),
      isColliding
    )
  }
  const onMeasureItemClicked = (id: string | null) => {
    if (id) {
      const item = props.measureObjects?.find((object) => object.id === id)
      if (item) {
        props.onClicked?.({ type: item.type, id })
      }
    }
  }

  const renderMeasureObject = (measureObject: MeasureObject) => {
    switch (measureObject.type) {
      case 'distance':
        return (
          <MeasureLine
            key={'measureLine_' + measureObject.id}
            line={measureObject}
            selected={props.selectedItem?.type === 'distance' && props.selectedItem.id === measureObject.id}
            onChange={(newLine) => {
              const measureline = props.measureObjects?.find((item) => item.id === measureObject.id)
              if (measureline) {
                props.onChangeMeasureObjects?.([
                  ...(props.measureObjects?.filter((ml) => ml.id !== measureObject.id) || []),
                  newLine,
                ])
              }
            }}
          />
        )
      case 'angle':
        return (
          <MeasureAngle
            key={'measureAngle_' + measureObject.id}
            angle={measureObject}
            selected={props.selectedItem?.type === 'angle' && props.selectedItem.id === measureObject.id}
            onChange={(newLine) => {
              const measureline = props.measureObjects?.find((item) => item.id === measureObject.id)
              if (measureline) {
                props.onChangeMeasureObjects?.([
                  ...(props.measureObjects?.filter((ml) => ml.id !== measureObject.id) || []),
                  newLine,
                ])
              }
            }}
          />
        )
      case 'coordinate':
        return (
          <MeasureCoordinate
            key={'measureCoordinate_' + measureObject.id}
            point={measureObject}
            selected={props.selectedItem?.type === 'coordinate' && props.selectedItem.id === measureObject.id}
            onChange={(newLine) => {
              const measureline = props.measureObjects?.find((item) => item.id === measureObject.id)
              if (measureline) {
                props.onChangeMeasureObjects?.([
                  ...(props.measureObjects?.filter((ml) => ml.id !== measureObject.id) || []),
                  newLine,
                ])
              }
            }}
          />
        )
      case 'area':
        return (
          <MeasureArea
            key={'measureArea_' + measureObject.id}
            area={measureObject}
            selected={props.selectedItem?.type === 'area' && props.selectedItem.id === measureObject.id}
            onChange={(newLine) => {
              const measureline = props.measureObjects?.find((item) => item.id === measureObject.id)
              if (measureline) {
                props.onChangeMeasureObjects?.([
                  ...(props.measureObjects?.filter((ml) => ml.id !== measureObject.id) || []),
                  newLine,
                ])
              }
            }}
          />
        )
      default:
        return null
    }
  }
  return (
    <MooringPlanContext.Provider value={{ mooringLayer: mooringLayer, kmapInstance }}>
      <MeasureProvider onClick={onMeasureItemClicked}>
        {renderDrawModes()}
        {props.mooredVessels.map((vessel, index) => (
          <Fragment key={'vessel_' + vessel.id + '_' + index}>
            <StaticVessel
              length={vessel.length}
              width={vessel.width}
              vesselId={vessel.id}
              position={vessel.mooredPosition}
              rotation={vessel.rotation}
              onChange={handleVesselChanged}
              editable={!vessel.isNeighbour && props.drawMode === null && !props.disableInteractions}
              style={vessel.isNeighbour ? neighbouringVesselStyle : mooredVesselStyle}
              selected={
                props.selectedItem?.type === 'vessel' && props.selectedItem.id === vessel.id && props.drawMode === null
              }
              label={(props.showLabels && vessel.label) || null}
            />
            {vessel.mooringLines?.map((mooringLine) => {
              const f = mooringLayer.getSource()?.getFeatureById(vessel.id)

              if (f instanceof Feature) {
                const coord = (f?.getGeometry() as Polygon)?.getCoordinates()[0][mooringLine.vertexIndex]
                if (!coord) return null
                const lonlat = toLonLat(coord)
                return (
                  <MooringLine
                    editable={!vessel.isNeighbour}
                    key={'mooringLine_vessel_' + vessel.id + '_line_' + mooringLine.id}
                    from={mooringLine.startPoint}
                    to={{ lon: lonlat[0], lat: lonlat[1] }}
                    vesselId={vessel.id}
                    selected={props.selectedItem?.type === 'mooringLine' && props.selectedItem.id === mooringLine.id}
                    id={mooringLine.id}
                    onChange={(from, to) => {
                      handleChangeMooringLine(vessel, mooringLine, from, to)
                    }}
                  />
                )
              } else {
                console.warn('Could not find vessel with id: ' + vessel.id)
                return null
              }
            })}
          </Fragment>
        ))}
        {props.measureObjects?.map(renderMeasureObject)}
      </MeasureProvider>
    </MooringPlanContext.Provider>
  )
}
